import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";
import { useState } from "react";

type DatePickerProps = {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  "aria-label"?: string;
};

export function DatePicker({
  value,
  onChange,
  placeholder = "Seleccionar fecha",
  minDate,
  maxDate,
  disabled = false,
  id,
  name,
  className,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          name={name}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "h-10 w-full justify-start gap-2 px-3 font-normal text-base",
            !value && "text-app-ink-3",
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 text-app-ink-2" aria-hidden="true" />
          {value ? format(value, "dd MMM yyyy", { locale: es }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          defaultMonth={value}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
          locale={es}
          weekStartsOn={1}
          disabled={buildDisabled(minDate, maxDate)}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}

function buildDisabled(minDate?: Date, maxDate?: Date) {
  if (minDate && maxDate) return [{ before: minDate }, { after: maxDate }];
  if (minDate) return { before: minDate };
  if (maxDate) return { after: maxDate };
  return undefined;
}
